// Ultra-aggressive jQuery reflow optimization
(function() {
  'use strict';
  
  // Cache for layout properties to avoid repeated calculations
  const layoutCache = new Map();
  const CACHE_DURATION = 16; // Cache for one frame (16ms)
  
  // Batch DOM operations to prevent layout thrashing
  const domOperations = {
    reads: [],
    writes: [],
    scheduled: false,
    frame: 0
  };
  
  function batchDOMOperations() {
    if (domOperations.scheduled) return;
    
    domOperations.scheduled = true;
    domOperations.frame = requestAnimationFrame(() => {
      const startTime = performance.now();
      
      // Execute all reads first (prevents interleaved read/write)
      domOperations.reads.forEach(fn => {
        try { fn(); } catch(e) { console.warn('Batched read error:', e); }
      });
      
      // Then execute all writes
      domOperations.writes.forEach(fn => {
        try { fn(); } catch(e) { console.warn('Batched write error:', e); }
      });
      
      // Clear operations
      domOperations.reads.length = 0;
      domOperations.writes.length = 0;
      domOperations.scheduled = false;
      
      // Performance monitoring
      const duration = performance.now() - startTime;
      if (duration > 16) {
        console.warn(`Long DOM operation: ${duration.toFixed(2)}ms`);
      }
    });
  }
  
  // Enhanced batch helpers with fallbacks
  window.batchRead = function(fn) {
    if (typeof fn !== 'function') return;
    domOperations.reads.push(fn);
    batchDOMOperations();
  };
  
  window.batchWrite = function(fn) {
    if (typeof fn !== 'function') return;
    domOperations.writes.push(fn);
    batchDOMOperations();
  };
  
  // Override problematic jQuery methods if jQuery is loaded
  if (window.jQuery) {
    const originalOffset = jQuery.fn.offset;
    const originalHeight = jQuery.fn.height;
    const originalWidth = jQuery.fn.width;
    
    // Cache offset calculations
    jQuery.fn.offset = function(...args) {
      if (args.length === 0) {
        // Reading offset
        const cacheKey = this[0] ? this[0].outerHTML : '';
        if (layoutCache.has(cacheKey)) {
          return layoutCache.get(cacheKey);
        }
        const result = originalOffset.apply(this, args);
        layoutCache.set(cacheKey, result);
        // Clear cache after next frame
        requestAnimationFrame(() => layoutCache.delete(cacheKey));
        return result;
      }
      // Setting offset
      return originalOffset.apply(this, args);
    };
    
    // Batch height/width operations
    jQuery.fn.height = function(...args) {
      if (args.length === 0) {
        // Reading height - batch it
        let result;
        batchRead(() => {
          result = originalHeight.apply(this, args);
        });
        return result;
      } else {
        // Setting height - batch it
        batchWrite(() => {
          originalHeight.apply(this, args);
        });
        return this;
      }
    };
    
    jQuery.fn.width = function(...args) {
      if (args.length === 0) {
        // Reading width - batch it
        let result;
        batchRead(() => {
          result = originalWidth.apply(this, args);
        });
        return result;
      } else {
        // Setting width - batch it
        batchWrite(() => {
          originalWidth.apply(this, args);
        });
        return this;
      }
    };
  }
  
  // Optimize common animation patterns
  window.optimizedAnimate = function(element, properties, duration = 300) {
    // Use CSS transforms instead of layout-triggering properties
    const optimizedProps = {};
    
    Object.keys(properties).forEach(prop => {
      if (prop === 'left' || prop === 'top') {
        // Use transform instead of left/top
        const x = properties.left || 0;
        const y = properties.top || 0;
        optimizedProps.transform = `translate(${x}px, ${y}px)`;
      } else if (prop === 'width' || prop === 'height') {
        // Use scale transform when possible
        optimizedProps[prop] = properties[prop];
      } else {
        optimizedProps[prop] = properties[prop];
      }
    });
    
    element.style.transition = `all ${duration}ms ease`;
    Object.assign(element.style, optimizedProps);
  };
  
  // Debounce scroll handlers to prevent excessive reflows
  let scrollTimeout;
  const originalAddEventListener = Element.prototype.addEventListener;
  
  Element.prototype.addEventListener = function(type, listener, options) {
    if (type === 'scroll' || type === 'resize') {
      const debouncedListener = function(...args) {
        clearTimeout(scrollTimeout);
        scrollTimeout = setTimeout(() => listener.apply(this, args), 16); // ~60fps
      };
      return originalAddEventListener.call(this, type, debouncedListener, options);
    }
    return originalAddEventListener.call(this, type, listener, options);
  };
  
})();